import { Button } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image, { type ImageProps } from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { AuthClientBoundary } from '../../AuthClientBoundary/AuthClientBoundary';
import { ContentGraphics } from '../../ContentGraphics/ContentGraphics';

type WinningsSectionImage = Omit<ImageProps, 'className' | 'fill' | 'sizes' | 'alt'> & Required<Pick<ImageProps, 'width' | 'height'>>;

type HomePageWinningsSectionProps = ComponentProps<'section'> & {
  namespace: string;
  image: WinningsSectionImage;
};

const { REGISTER } = ROUTES;

function HomePageWinningsSection({ namespace, className, image, ...props }: HomePageWinningsSectionProps) {
  const t = useTranslations(namespace);

  const classes = {
    section: twMerge('pt-10 pb-10 md:pt-20 md:px-10 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-y-10 gap-x-10 xl:gap-x-30', className),
    detailsContainer: 'flex flex-col items-center gap-4 md:gap-5 xl:w-140',
    detailsTextContainer: 'flex flex-col items-center text-center',
    statement: 'text-foreground-subtle mb-3 md:mb-4 lg:heading-3',
    title: 'display-xl-strong mb-1 md:mb-2 lg:heading-1',
    subTitle: 'text-foreground-subtle lg:heading-4',
  };

  return (
    <section className={classes.section} data-testid="winnings-section" data-track-container="Discover Winners" {...props}>
      <ContentGraphics>
        <Image {...image} alt={t('image_alt')} />
      </ContentGraphics>
      <div className={classes.detailsContainer} data-track-container="Winnings">
        <div className={classes.detailsTextContainer}>
          <h4 className={classes.statement}>{t('statement')}</h4>
          <p className={classes.title}>{t('title')}</p>
          <h5 className={classes.subTitle}>{t('sub_title')}</h5>
        </div>
        <AuthClientBoundary.LoggedOut>
          <Button variant="primary" size="l" className="md:hidden" asChild data-track-name="Register">
            <Link href={REGISTER}>{t('cta')}</Link>
          </Button>
          <Button variant="primary" size="m" className="hidden md:inline-flex" asChild data-track-name="Register">
            <Link href={REGISTER}>{t('cta')}</Link>
          </Button>
        </AuthClientBoundary.LoggedOut>
      </div>
    </section>
  );
}

export { HomePageWinningsSection };
export type { WinningsSectionImage };
